import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Text, Button} from '@/ui-kits';
import {FilterType} from '@/constants/enums';
import {ExperienceFilterTable, RatingFilterTable, ChargesFilterTable, AddExperienceModal, AddRatingModal, AddChargesModal} from '@/components';
import {useToast} from '@/hooks';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {
  getFilterOptionsRequest,
  addFilterRequest,
  editFilterRequest,
  resetPatientAdminMessage,
} from '@/services/redux/slices/patientAdminSlice';
import {
  filterOptionsSelector,
  patientAdminStatus,
  patientAdminSuccessSelector,
  patientAdminFailureSelector,
} from '@/services/redux/selectors/patientAdminSelector';
import {AddFilterRequestPayload, EditFilterRequestPayload} from '@/interface';

interface FilterItem {
  id: string;
  label: string;
  from: number | string;
  to: number | string;
  status: boolean;
  order?: number;
}

export const FilterConfiguration: React.FC = () => {
  // ==============================
  // hooks
  // ==============================
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ==============================
  // selectors
  // ==============================
  const filterOptions = useSelector(filterOptionsSelector);
  const isLoading = useSelector(patientAdminStatus);
  const patientAdminSuccess = useSelector(patientAdminSuccessSelector) as SuccessCodes;
  const patientAdminFailure = useSelector(patientAdminFailureSelector) as ErrorCodes;

  // ==============================
  // states
  // ==============================
  const [experienceEditingId, setExperienceEditingId] = useState<string | null>(null);
  const [experienceEditForm, setExperienceEditForm] = useState<Partial<FilterItem>>({});
  const [ratingEditingId, setRatingEditingId] = useState<string | null>(null);
  const [ratingEditForm, setRatingEditForm] = useState<Partial<FilterItem>>({});
  const [chargesEditingId, setChargesEditingId] = useState<string | null>(null);
  const [chargesEditForm, setChargesEditForm] = useState<Partial<FilterItem>>({});
  const [isExperienceModalOpen, setIsExperienceModalOpen] = useState(false);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [isChargesModalOpen, setIsChargesModalOpen] = useState(false);

  // ==============================
  // functions
  // ==============================
  // Helper function to map API response to FilterItem format
  const mapApiResponseToFilterItem = useCallback(
    (apiFilters: {id: number; label: string; min: number; max: number; status: boolean}[]): FilterItem[] => {
      return apiFilters.map((filter, index) => ({
        id: String(filter.id),
        label: filter.label,
        from: filter.min,
        to: filter.max === 999999 || filter.max === Number.MAX_SAFE_INTEGER ? 'infinity' : filter.max,
        status: filter.status,
        order: index + 1,
      }));
    },
    []
  );

  // Transform Redux filter options to FilterItem format using useMemo
  const experienceFilters = useMemo(() => {
    if (!filterOptions) return [];
    const mapped = mapApiResponseToFilterItem(filterOptions.experienceFilters);
    return mapped.sort((a, b) => parseFloat(String(a.from)) - parseFloat(String(b.from)));
  }, [filterOptions, mapApiResponseToFilterItem]);

  const ratingFilters = useMemo(() => {
    if (!filterOptions) return [];
    const mapped = mapApiResponseToFilterItem(filterOptions.ratingFilters);
    return mapped.sort((a, b) => parseFloat(String(a.from)) - parseFloat(String(b.from)));
  }, [filterOptions, mapApiResponseToFilterItem]);

  const chargesFilters = useMemo(() => {
    if (!filterOptions) return [];
    const mapped = mapApiResponseToFilterItem(filterOptions.chargesFilters);
    return mapped.sort((a, b) => parseFloat(String(a.from)) - parseFloat(String(b.from)));
  }, [filterOptions, mapApiResponseToFilterItem]);

  // Experience handlers
  const handleExperienceEdit = useCallback((item: FilterItem) => {
    setExperienceEditingId(item.id);
    setExperienceEditForm({
      label: item.label,
      from: item.from,
      to: item.to,
      status: item.status,
      order: item.order,
    });
  }, []);

  const handleExperienceSave = useCallback(
    (id: string) => {
      const currentItem = experienceFilters.find(item => item.id === id);
      if (!currentItem) return;

      let updatedLabel = experienceEditForm.label ?? currentItem.label;
      const updatedFrom = experienceEditForm.from ?? currentItem.from;
      const updatedTo = experienceEditForm.to ?? currentItem.to;
      const updatedStatus = experienceEditForm.status ?? currentItem.status;

      // For experience filters, automatically append "years" to label if not already present
      updatedLabel = updatedLabel.trim();
      const lowerLabel = updatedLabel.toLowerCase();
      if (!lowerLabel.endsWith('years') && !lowerLabel.endsWith('year')) {
        updatedLabel = updatedLabel + ' years';
      }

      const maxValue = updatedTo === 'infinity' ? 999999 : parseFloat(String(updatedTo));
      const minValue = parseFloat(String(updatedFrom));

      const payload: EditFilterRequestPayload = {
        id: Number(id),
        label: updatedLabel,
        min_value: minValue,
        max_value: maxValue,
        status: updatedStatus,
      };

      dispatch(editFilterRequest(payload));
    },
    [experienceEditForm, experienceFilters, dispatch]
  );

  const handleExperienceCancel = useCallback(() => {
    setExperienceEditingId(null);
    setExperienceEditForm({});
  }, []);

  const handleExperienceEditField = useCallback((field: string, value: any) => {
    setExperienceEditForm(p => ({
      ...p,
      [field]: value,
    }));
  }, []);

  const handleExperienceStatusToggle = useCallback(
    (id: string) => {
      const currentItem = experienceFilters.find(item => item.id === id);
      if (!currentItem) return;

      const maxValue = currentItem.to === 'infinity' ? 999999 : parseFloat(String(currentItem.to));
      const minValue = parseFloat(String(currentItem.from));

      const payload: EditFilterRequestPayload = {
        id: Number(id),
        label: currentItem.label,
        min_value: minValue,
        max_value: maxValue,
        status: !currentItem.status,
      };

      dispatch(editFilterRequest(payload));
    },
    [experienceFilters, dispatch]
  );

  // Rating handlers
  const handleRatingEdit = useCallback((item: FilterItem) => {
    setRatingEditingId(item.id);
    setRatingEditForm({
      label: item.label,
      from: item.from,
      to: item.to,
      status: item.status,
      order: item.order,
    });
  }, []);

  const handleRatingSave = useCallback(
    (id: string) => {
      const currentItem = ratingFilters.find(item => item.id === id);
      if (!currentItem) return;

      const updatedLabel = ratingEditForm.label ?? currentItem.label;
      const updatedFrom = ratingEditForm.from ?? currentItem.from;
      const updatedTo = ratingEditForm.to ?? currentItem.to;
      const updatedStatus = ratingEditForm.status ?? currentItem.status;

      const maxValue = updatedTo === 'infinity' ? 999999 : parseFloat(String(updatedTo));
      const minValue = parseFloat(String(updatedFrom));

      const payload: EditFilterRequestPayload = {
        id: Number(id),
        label: updatedLabel,
        min_value: minValue,
        max_value: maxValue,
        status: updatedStatus,
      };

      dispatch(editFilterRequest(payload));
    },
    [ratingEditForm, ratingFilters, dispatch]
  );

  const handleRatingCancel = useCallback(() => {
    setRatingEditingId(null);
    setRatingEditForm({});
  }, []);

  const handleRatingEditField = useCallback((field: string, value: any) => {
    setRatingEditForm(p => ({
      ...p,
      [field]: value,
    }));
  }, []);

  const handleRatingStatusToggle = useCallback(
    (id: string) => {
      const currentItem = ratingFilters.find(item => item.id === id);
      if (!currentItem) return;

      const maxValue = currentItem.to === 'infinity' ? 999999 : parseFloat(String(currentItem.to));
      const minValue = parseFloat(String(currentItem.from));

      const payload: EditFilterRequestPayload = {
        id: Number(id),
        label: currentItem.label,
        min_value: minValue,
        max_value: maxValue,
        status: !currentItem.status,
      };

      dispatch(editFilterRequest(payload));
    },
    [ratingFilters, dispatch]
  );

  // Charges handlers
  const handleChargesEdit = useCallback((item: FilterItem) => {
    setChargesEditingId(item.id);
    setChargesEditForm({
      label: item.label,
      from: item.from,
      to: item.to,
      status: item.status,
      order: item.order,
    });
  }, []);

  const handleChargesSave = useCallback(
    (id: string) => {
      const currentItem = chargesFilters.find(item => item.id === id);
      if (!currentItem) return;

      let updatedLabel = chargesEditForm.label ?? currentItem.label;
      const updatedFrom = chargesEditForm.from ?? currentItem.from;
      const updatedTo = chargesEditForm.to ?? currentItem.to;
      const updatedStatus = chargesEditForm.status ?? currentItem.status;

      const isGreaterThan = updatedTo === 'infinity';
      
      // For greater_than format, ensure label ends with "+" (but not double "+")
      if (isGreaterThan) {
        updatedLabel = updatedLabel.trim();
        // Remove trailing "+" if present, then add one
        updatedLabel = updatedLabel.replace(/\++$/, '') + '+';
      }

      const maxValue = updatedTo === 'infinity' ? 999999 : parseFloat(String(updatedTo));
      const minValue = parseFloat(String(updatedFrom));

      const payload: EditFilterRequestPayload = {
        id: Number(id),
        label: updatedLabel,
        min_value: minValue,
        max_value: maxValue,
        status: updatedStatus,
      };

      dispatch(editFilterRequest(payload));
    },
    [chargesEditForm, chargesFilters, dispatch]
  );

  const handleChargesCancel = useCallback(() => {
    setChargesEditingId(null);
    setChargesEditForm({});
  }, []);

  const handleChargesEditField = useCallback((field: string, value: any) => {
    setChargesEditForm(p => ({
      ...p,
      [field]: value,
    }));
  }, []);

  const handleChargesStatusToggle = useCallback(
    (id: string) => {
      const currentItem = chargesFilters.find(item => item.id === id);
      if (!currentItem) return;

      const maxValue = currentItem.to === 'infinity' ? 999999 : parseFloat(String(currentItem.to));
      const minValue = parseFloat(String(currentItem.from));

      const payload: EditFilterRequestPayload = {
        id: Number(id),
        label: currentItem.label,
        min_value: minValue,
        max_value: maxValue,
        status: !currentItem.status,
      };

      dispatch(editFilterRequest(payload));
    },
    [chargesFilters, dispatch]
  );

  const handleAddExperience = useCallback(
    (data: {
      label: string;
      format: 'between' | 'greater_than';
      from: string;
      to: string;
      status: boolean;
    }) => {
      const maxValue = data.to === 'infinity' ? 999999 : parseFloat(data.to);
      let formattedLabel = data.label.trim();
      
      // For greater_than format, ensure "+" is present (before "years" if any)
      if (data.format === 'greater_than' && !formattedLabel.includes('+')) {
        const withoutSuffix = formattedLabel.replace(/\s*(years?)$/i, '').trim();
        formattedLabel = withoutSuffix + '+';
      }
      
      // Ensure "years" suffix for both formats
      const lowerLabel = formattedLabel.toLowerCase();
      if (!lowerLabel.endsWith('years') && !lowerLabel.endsWith('year')) {
        formattedLabel = formattedLabel + ' years';
      }

      const payload: AddFilterRequestPayload = {
        type: FilterType.Experience,
        label: formattedLabel,
        min_value: parseFloat(data.from),
        max_value: maxValue,
        status: data.status,
      };

      dispatch(addFilterRequest(payload));
      setIsExperienceModalOpen(false);
    },
    [dispatch]
  );

  const handleAddRating = useCallback(
    (data: {
      label: string;
      from: string;
      to: string;
      status: boolean;
    }) => {
      const payload: AddFilterRequestPayload = {
        type: FilterType.Rating,
        label: data.label,
        min_value: parseFloat(data.from),
        max_value: parseFloat(data.to),
        status: data.status,
      };

      dispatch(addFilterRequest(payload));
      setIsRatingModalOpen(false);
    },
    [dispatch]
  );

  const handleAddCharges = useCallback(
    (data: {
      label: string;
      format: 'between' | 'greater_than';
      from: string;
      to: string;
      status: boolean;
    }) => {
      const maxValue = data.to === 'infinity' ? 999999 : parseFloat(data.to);
      let formattedLabel = data.label.trim();
      
      // For greater_than format, append "+" if not already ends with "+"
      if (data.format === 'greater_than') {
        // Check if label already ends with "+" (with or without trailing spaces)
        if (!formattedLabel.trimEnd().endsWith('+')) {
          formattedLabel = formattedLabel + '+';
        }
      }

      const payload: AddFilterRequestPayload = {
        type: FilterType.Charges,
        label: formattedLabel,
        min_value: parseFloat(data.from),
        max_value: maxValue,
        status: data.status,
      };

      dispatch(addFilterRequest(payload));
      setIsChargesModalOpen(false);
    },
    [dispatch]
  );

  // ==============================
  // side effects
  // ==============================
  useEffect(() => {
    dispatch(getFilterOptionsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (patientAdminSuccess) {
      if (['S-10008', 'S-10019', 'S-10011', 'S-10065', 'S-10066', 'S-10067', 'S-10068', 'S-10069', 'S-10070'].includes(patientAdminSuccess)) {
        showToast(getSuccessMessage(patientAdminSuccess), 'success');
        
        if (['S-10008', 'S-10065', 'S-10066', 'S-10067'].includes(patientAdminSuccess)) {
          setIsExperienceModalOpen(false);
          setIsRatingModalOpen(false);
          setIsChargesModalOpen(false);
        }
      
        if (['S-10068', 'S-10069', 'S-10070', 'S-10019'].includes(patientAdminSuccess)) {
          setExperienceEditingId(null);
          setExperienceEditForm({});
          setRatingEditingId(null);
          setRatingEditForm({});
          setChargesEditingId(null);
          setChargesEditForm({});
        }
        
        // Refresh filter options to get updated data after any successful operation
        dispatch(getFilterOptionsRequest());
      }
    }
    if (patientAdminFailure) {
      showToast(getErrorMessage(patientAdminFailure), 'failure');
    }
    return () => {
      dispatch(resetPatientAdminMessage());
    };
  }, [patientAdminSuccess, patientAdminFailure, dispatch, showToast]);

  return (
    <div className="flex flex-col gap-4 sm:gap-6 md:gap-8">
      <div className="flex flex-col gap-1 sm:gap-2">
        <Text variant="heading" className="text-deep_blue font-FigtreeSemiBold mt-2 sm:mt-4 text-lg sm:text-xl md:text-2xl">
          Filter Configuration
        </Text>
        <Text variant="free" className="text-sm sm:text-base md:text-lg lg:text-xl text-ui_input_border_dark font-FigtreeRegular">
          Manage filter options shown to patients on the Specialist Page.
        </Text>
      </div>

      {/* Experience Filter Settings */}
      <div className="bg-white rounded-xl sm:rounded-2xl border border-ui_input_border p-3 sm:p-4 md:p-6 flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-col min-[375px]:flex-row justify-between items-start min-[375px]:items-center gap-2 min-[375px]:gap-4">
          <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-sm sm:text-lg md:text-xl lg:text-2xl flex-shrink-0 min-w-0">
            Experience Filter Settings
          </Text>
          <Button
            variant="primary"
            title="Add Experience"
            leftIcon="plus_white"
            iconClassName="size-2.5 sm:size-3"
            className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm flex-shrink-0"
            onClick={() => setIsExperienceModalOpen(true)}
          />
        </div>
        <ExperienceFilterTable
          data={experienceFilters}
          editingId={experienceEditingId}
          editForm={experienceEditForm}
          onEdit={handleExperienceEdit}
          onSave={handleExperienceSave}
          onCancel={handleExperienceCancel}
          onEditField={handleExperienceEditField}
          onStatusToggle={handleExperienceStatusToggle}
          loading={isLoading}
        />
      </div>

      {/* Rating Filter Settings */}
      <div className="bg-white rounded-xl sm:rounded-2xl border border-ui_input_border p-3 sm:p-4 md:p-6 flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-col min-[375px]:flex-row justify-between items-start min-[375px]:items-center gap-2 min-[375px]:gap-4">
          <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-sm sm:text-lg md:text-xl lg:text-2xl flex-shrink-0 min-w-0">
            Ratings Filter Settings
          </Text>
          <Button
            variant="primary"
            title="Add Ratings"
            leftIcon="plus_white"
            iconClassName="size-2.5 sm:size-3"
            className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm flex-shrink-0"
            onClick={() => setIsRatingModalOpen(true)}
          />
        </div>
        <RatingFilterTable
          data={ratingFilters}
          editingId={ratingEditingId}
          editForm={ratingEditForm}
          onEdit={handleRatingEdit}
          onSave={handleRatingSave}
          onCancel={handleRatingCancel}
          onEditField={handleRatingEditField}
          onStatusToggle={handleRatingStatusToggle}
          loading={isLoading}
        />
      </div>

      {/* Charges Filter Settings */}
      <div className="bg-white rounded-xl sm:rounded-2xl border border-ui_input_border p-3 sm:p-4 md:p-6 flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-col min-[375px]:flex-row justify-between items-start min-[375px]:items-center gap-2 min-[375px]:gap-4">
          <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-sm sm:text-lg md:text-xl lg:text-2xl flex-shrink-0 min-w-0">
            Charges Filter Settings
          </Text>
          <Button
            variant="primary"
            title="Add Charges"
            leftIcon="plus_white"
            iconClassName="size-2.5 sm:size-3"
            className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm flex-shrink-0"
            onClick={() => setIsChargesModalOpen(true)}
          />
        </div>
        <ChargesFilterTable
          data={chargesFilters}
          editingId={chargesEditingId}
          editForm={chargesEditForm}
          onEdit={handleChargesEdit}
          onSave={handleChargesSave}
          onCancel={handleChargesCancel}
          onEditField={handleChargesEditField}
          onStatusToggle={handleChargesStatusToggle}
          loading={isLoading}
        />
      </div>

      {/* Modals */}
      {isExperienceModalOpen && <AddExperienceModal
        open={isExperienceModalOpen}
        onClose={() => setIsExperienceModalOpen(false)}
        onSave={handleAddExperience}
      />}

      {isRatingModalOpen && <AddRatingModal
        open={isRatingModalOpen}
        onClose={() => setIsRatingModalOpen(false)}
        onSave={handleAddRating}
      />}
      
      {isChargesModalOpen && <AddChargesModal
        open={isChargesModalOpen}
        onClose={() => setIsChargesModalOpen(false)}
        onSave={handleAddCharges}
      />}
    </div>
  );
};
